import { isEditableElementFocused } from '@src/services/shortcuts/keyboard';
import {
  applyLinkedHighlight,
  clearLinkedHighlights,
  EMPTY_LINKED_MIRROR,
  LINKED_ORIGINAL_HIGHLIGHT,
  LINKED_TRANSLATED_HIGHLIGHT,
  type LinkedPhrase,
  resolveLinkedMirror,
} from '@src/utility/linkedCrossLanguageHighlight';
import { type RefObject, useEffect, useRef } from 'react';

interface UseLinkedCrossLanguageHighlightParams {
  /** Opt-in (D9). When false the hook does nothing and registers no listener. */
  enabled: boolean;
  /** The original (left) pane element — its `[data-initial-index]` word spans. */
  originalPaneRef: RefObject<HTMLElement>;
  /** The translated (middle) pane element — its `[data-initial-index]` phrase spans. */
  translatedPaneRef: RefObject<HTMLElement>;
  /** The language's translation phrases (source-index provenance). */
  phrases: ReadonlyArray<LinkedPhrase>;
}

/** Collect the mirror KEYS of every `[data-initial-index]` span in `pane` that
 *  the selection range intersects. PLAT-10611 (F5): id-FIRST per span — a span
 *  with a `data-source-uuid` contributes its stable id; one without contributes
 *  its integer `data-initial-index` (§6). Scoped to the pane element so a sibling
 *  transcript instance elsewhere on the page is never read (CLAUDE.md #12).
 *  O(spans-in-pane) per selection change — fine for an interaction path, not a
 *  render hot path. */
function collectSelectedKeys(
  pane: HTMLElement,
  range: Range,
): { ids: Set<string>; ints: Set<number> } {
  const ids = new Set<string>();
  const ints = new Set<number>();
  const spans = pane.querySelectorAll<HTMLElement>('[data-initial-index]');
  spans.forEach(span => {
    if (!range.intersectsNode(span)) return;
    const uuid = span.getAttribute('data-source-uuid');
    if (uuid != null && uuid !== '') {
      ids.add(uuid);
      return;
    }
    const raw = span.getAttribute('data-initial-index');
    if (raw == null) return;
    const n = Number(raw);
    if (Number.isFinite(n)) ints.add(n);
  });
  return { ids, ints };
}

/**
 * PLAT-10287 — Linked cross-language selection highlight.
 *
 * Watches the text selection inside the two Translation Review panes. When the
 * reviewer selects words in one pane, the corresponding words in the other pane
 * receive a subtle highlight (via the CSS Custom Highlight API, scoped per pane
 * by range provenance). Clears when the selection collapses or leaves the panes.
 *
 * Passive and transient: it never mutates the translation, persists nothing, and
 * registers no listeners when `enabled` is false (D9 containment — the shared
 * `Transcript` panes behave exactly as before when the workspace doesn't opt in).
 */
export function useLinkedCrossLanguageHighlight({
  enabled,
  originalPaneRef,
  translatedPaneRef,
  phrases,
}: UseLinkedCrossLanguageHighlightParams): void {
  // Read the latest phrases inside the listener without re-subscribing on every
  // store-driven identity change (the array is a fresh reference each render).
  const phrasesRef = useRef(phrases);
  phrasesRef.current = phrases;

  useEffect(() => {
    if (!enabled) return;

    let rafId: number | null = null;

    const recompute = () => {
      rafId = null;
      const original = originalPaneRef.current;
      const translated = translatedPaneRef.current;
      if (!original || !translated) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        clearLinkedHighlights();
        return;
      }
      const range = selection.getRangeAt(0);
      const anchor = selection.anchorNode;
      if (!anchor) {
        clearLinkedHighlights();
        return;
      }

      // Which pane owns the selection? (Scope by the anchor node.)
      const fromPane = original.contains(anchor)
        ? 'original'
        : translated.contains(anchor)
          ? 'translated'
          : null;
      if (fromPane === null) {
        // Selection is outside both review panes — leave their highlights clear.
        clearLinkedHighlights();
        return;
      }

      const sourcePaneEl = fromPane === 'original' ? original : translated;
      const selected = collectSelectedKeys(sourcePaneEl, range);
      const mirror = resolveLinkedMirror(
        selected.ids,
        selected.ints,
        fromPane,
        phrasesRef.current,
      );

      // Paint the mirror set in the OPPOSITE pane; clear the source-pane name
      // (the browser already shows the native selection there).
      if (fromPane === 'original') {
        applyLinkedHighlight(translated, mirror, LINKED_TRANSLATED_HIGHLIGHT);
        applyLinkedHighlight(
          original,
          EMPTY_LINKED_MIRROR,
          LINKED_ORIGINAL_HIGHLIGHT,
        );
      } else {
        applyLinkedHighlight(original, mirror, LINKED_ORIGINAL_HIGHLIGHT);
        applyLinkedHighlight(
          translated,
          EMPTY_LINKED_MIRROR,
          LINKED_TRANSLATED_HIGHLIGHT,
        );
      }
    };

    // `selectionchange` fires rapidly during a drag; coalesce to one rAF.
    const onSelectionChange = () => {
      // PLAT-11650 — a caret moving inside the in-place phrase field is not a
      // cross-language selection.
      if (isEditableElementFocused()) return;
      if (rafId !== null) return;
      rafId = requestAnimationFrame(recompute);
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      if (rafId !== null) cancelAnimationFrame(rafId);
      clearLinkedHighlights();
    };
  }, [enabled, originalPaneRef, translatedPaneRef]);
}
